import React from 'react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { exportToCSV } from '../../../services/exportService';

const RevenueDrivers = ({ onShowEvidence }) => {
  const revenueByProgram = [
    { program: "Enterprise Solutions", revenue: 4250000, growth: 12.5 },
    { program: "Cloud Services", revenue: 3800000, growth: 18.3 },
    { program: "Consulting", revenue: 2900000, growth: 8.7 },
    { program: "Support & Maintenance", revenue: 2100000, growth: 5.2 },
    { program: "Training Services", revenue: 1450000, growth: -2.1 }
  ];

  const revenueTrend = [
    { month: "Aug 2025", revenue: 12500000, target: 12000000 },
    { month: "Sep 2025", revenue: 13200000, target: 12500000 },
    { month: "Oct 2025", revenue: 13800000, target: 13000000 },
    { month: "Nov 2025", revenue: 14100000, target: 13500000 },
    { month: "Dec 2025", revenue: 14500000, target: 14000000 },
    { month: "Jan 2026", revenue: 14500000, target: 14500000 }
  ];

  const topContributors = [
    { name: "Global Tech Corp", revenue: 2850000, percentage: 19.7, trend: "up" },
    { name: "Innovation Systems Ltd", revenue: 2340000, percentage: 16.1, trend: "up" },
    { name: "Digital Ventures Inc", revenue: 1920000, percentage: 13.2, trend: "stable" },
    { name: "Enterprise Solutions Co", revenue: 1680000, percentage: 11.6, trend: "up" },
    { name: "Cloud Partners LLC", revenue: 1450000, percentage: 10.0, trend: "down" }
  ];

  const lowRevenueAreas = [
    { 
      area: "Legacy Product Line", 
      revenue: 450000, 
      decline: -15.3, 
      issue: "Product obsolescence and declining market demand",
      recommendation: "Consider phasing out or modernizing product offerings"
    },
    { 
      area: "Regional Office - Southeast", 
      revenue: 680000, 
      decline: -8.7, 
      issue: "High competition and pricing pressure in the region",
      recommendation: "Implement targeted marketing and competitive pricing strategy"
    },
    { 
      area: "Small Business Segment", 
      revenue: 520000, 
      decline: -12.1, 
      issue: "Low conversion rates and high customer acquisition costs",
      recommendation: "Optimize sales funnel and reduce acquisition costs"
    }
  ];

  const COLORS = ['#1E3A5F', '#4A90A4', '#E67E22', '#38A169', '#D69E2E'];

  const getTrendIcon = (trend) => {
    if (trend === 'up') return { name: 'TrendingUp', color: 'var(--color-success)' };
    if (trend === 'down') return { name: 'TrendingDown', color: 'var(--color-error)' };
    return { name: 'Minus', color: 'var(--color-muted-foreground)' };
  };

  const handleExportCSV = () => {
    // Export revenue by program
    const programData = revenueByProgram?.map(item => ({
      Program: item?.program,
      Revenue: `$${(item?.revenue / 1000000)?.toFixed(2)}M`,
      'Growth %': `${item?.growth}%`
    }));

    exportToCSV({
      title: 'Revenue_Drivers_By_Program',
      data: programData
    });
  };

  const handleExportContributorsCSV = () => {
    // Export top contributors
    const contributorsData = topContributors?.map(item => ({
      Name: item?.name,
      Revenue: `$${(item?.revenue / 1000000)?.toFixed(2)}M`,
      'Percentage %': `${item?.percentage}%`,
      Trend: item?.trend
    }));

    exportToCSV({
      title: 'Revenue_Top_Contributors',
      data: contributorsData
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-md bg-primary/10">
            <Icon name="DollarSign" size={20} color="var(--color-primary)" />
          </div>
          <div>
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground">Revenue Drivers</h2>
            <p className="text-sm text-muted-foreground font-caption">Comprehensive revenue analysis and performance tracking</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => onShowEvidence?.('revenue', 'Revenue Drivers')}
            variant="outline"
            size="sm"
            iconName="FileSearch"
          >
            View Evidence
          </Button>
          <Button
            onClick={handleExportCSV}
            variant="outline"
            size="sm"
            iconName="Download"
          >
            Export CSV
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-card rounded-lg border border-border p-4 md:p-6">
          <h3 className="text-base md:text-lg font-heading font-semibold text-foreground mb-4">Revenue by Program / Business Line</h3>
          <div className="h-64 md:h-80" aria-label="Revenue by Program Bar Chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={revenueByProgram}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis 
                  dataKey="program" 
                  tick={{ fill: 'var(--color-muted-foreground)', fontSize: 12 }}
                  angle={-45}
                  textAnchor="end"
                  height={100}
                />
                <YAxis 
                  tick={{ fill: 'var(--color-muted-foreground)', fontSize: 12 }}
                  tickFormatter={(value) => `$${(value / 1000000)?.toFixed(1)}M`}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'var(--color-popover)', 
                    border: '1px solid var(--color-border)',
                    borderRadius: '8px'
                  }}
                  formatter={(value) => [`$${(value / 1000000)?.toFixed(2)}M`, 'Revenue']}
                />
                <Legend />
                <Bar dataKey="revenue" fill="var(--color-primary)" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-3">
            {revenueByProgram?.map((item, index) => (
              <div key={index} className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS?.[index % COLORS?.length] }} />
                <span className="text-xs text-muted-foreground font-caption truncate">{item?.program}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-card rounded-lg border border-border p-4 md:p-6">
          <h3 className="text-base md:text-lg font-heading font-semibold text-foreground mb-4">Revenue Trend by Time</h3>
          <div className="h-64 md:h-80" aria-label="Revenue Trend Line Chart">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={revenueTrend}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis 
                  dataKey="month" 
                  tick={{ fill: 'var(--color-muted-foreground)', fontSize: 12 }}
                />
                <YAxis 
                  tick={{ fill: 'var(--color-muted-foreground)', fontSize: 12 }}
                  tickFormatter={(value) => `$${(value / 1000000)?.toFixed(0)}M`}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'var(--color-popover)', 
                    border: '1px solid var(--color-border)',
                    borderRadius: '8px'
                  }}
                  formatter={(value) => [`$${(value / 1000000)?.toFixed(2)}M`, '']}
                />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="revenue" 
                  stroke="var(--color-primary)" 
                  strokeWidth={3}
                  dot={{ fill: 'var(--color-primary)', r: 5 }}
                  name="Actual Revenue"
                />
                <Line 
                  type="monotone" 
                  dataKey="target" 
                  stroke="var(--color-secondary)" 
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  dot={{ fill: 'var(--color-secondary)', r: 4 }}
                  name="Target Revenue"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-card rounded-lg border border-border p-4 md:p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base md:text-lg font-heading font-semibold text-foreground">Top Revenue Contributors</h3>
            <Button
              onClick={handleExportContributorsCSV}
              variant="ghost"
              size="xs"
              iconName="FileText"
            >
              Export
            </Button>
          </div>
          <div className="space-y-4">
            {topContributors?.map((contributor, index) => {
              const trendIcon = getTrendIcon(contributor?.trend);
              return (
                <div key={index} className="flex items-center justify-between p-3 rounded-md bg-muted/50 hover:bg-muted transition-smooth">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 flex-shrink-0">
                      <span className="text-sm font-semibold text-primary font-data">{index + 1}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{contributor?.name}</p>
                      <p className="text-xs text-muted-foreground font-caption">{contributor?.percentage}% of total revenue</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className="text-sm font-semibold text-foreground font-data whitespace-nowrap">
                      ${(contributor?.revenue / 1000000)?.toFixed(2)}M
                    </span>
                    <Icon name={trendIcon?.name} size={18} color={trendIcon?.color} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-card rounded-lg border border-border p-4 md:p-6">
          <h3 className="text-base md:text-lg font-heading font-semibold text-foreground mb-4">Low Revenue Areas</h3>
          <div className="space-y-4">
            {lowRevenueAreas?.map((area, index) => (
              <div key={index} className="p-4 rounded-md border border-warning/30 bg-warning/5">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-semibold text-foreground mb-1">{area?.area}</h4>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-data text-muted-foreground">
                        ${(area?.revenue / 1000)?.toFixed(0)}K
                      </span>
                      <span className="text-xs font-semibold text-error font-data">
                        {area?.decline}%
                      </span>
                    </div>
                  </div>
                  <Icon name="AlertTriangle" size={20} color="var(--color-warning)" />
                </div>
                <p className="text-xs text-muted-foreground mb-2 font-caption">{area?.issue}</p>
                <div className="flex items-start gap-2 p-2 rounded bg-background/50">
                  <Icon name="Lightbulb" size={14} color="var(--color-accent)" className="flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-foreground font-caption">{area?.recommendation}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RevenueDrivers;